import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["headerProps", "children", "locked", "loading", "title", "iconTitle", "titleExtra", "icon", "iconVisibleAlways", "withTabs", "classes", "onCardClick", "onIconClick", "onRefresh", "reloadLabel", "iconLabel", "readonly", "tabIndex", "contentPadding", "headerPadding", "role", "componentType"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/*
 * © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React from 'react';
import { useIntl } from 'react-intl';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import { EmbersLoading } from '../loading/loading';
import { RefreshButton } from '../table/topControls/refreshButton/refreshButton';
import { TextTooltip } from '../tooltip/textTooltip/textTooltip';
import { EmbersTypography } from '../typography';
import { EMBERS_CARD_ROOT_PREFIX, createEmbersCardTheme } from './cardStyles';
import classnames from 'classnames';
import { isTouchDevices } from '../utils/platformUtils';
import { EmbersInnerThemeProvider } from '../rtlProvider';
import { onEnterKeyPress } from '../utils/wcagUtils';
export var CardContentHeader = function CardContentHeader(_ref) {
  var _classnames;
  var title = _ref.title,
    iconTitle = _ref.iconTitle,
    titleExtra = _ref.titleExtra,
    icon = _ref.icon,
    iconLabel = _ref.iconLabel,
    _ref$iconVisibleAlway = _ref.iconVisibleAlways,
    iconVisibleAlways = _ref$iconVisibleAlway === void 0 ? false : _ref$iconVisibleAlway,
    onIconClick = _ref.onIconClick,
    onRefresh = _ref.onRefresh,
    reloadLabel = _ref.reloadLabel,
    componentType = _ref.componentType;
  var intl = useIntl();
  var onCardIconClick = function onCardIconClick(e) {
    e.stopPropagation();
    onIconClick === null || onIconClick === void 0 || onIconClick(e);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames("".concat(EMBERS_CARD_ROOT_PREFIX, "-content-header"), (_classnames = {}, _defineProperty(_classnames, "".concat(EMBERS_CARD_ROOT_PREFIX, "-content-header-hasIcon"), icon), _defineProperty(_classnames, "".concat(EMBERS_CARD_ROOT_PREFIX, "-content-header-hasRefresh"), onRefresh), _classnames))
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(EMBERS_CARD_ROOT_PREFIX, "-title")
  }, iconTitle ? /*#__PURE__*/React.createElement(EmbersTypography, {
    variant: "subtitle1",
    componentType: componentType || 'h3',
    className: "".concat(EMBERS_CARD_ROOT_PREFIX, "-title-iconTitle")
  }, iconTitle) : /*#__PURE__*/React.createElement(TextTooltip, {
    tooltipProps: titleExtra ? {
      title: titleExtra,
      type: 'info'
    } : undefined,
    typographyProps: {
      variant: 'subtitle1',
      componentType: componentType || 'h3',
      noWrap: false
    }
  }, title)), icon && /*#__PURE__*/React.createElement("div", {
    className: classnames({
      'card-icon': true,
      'visible-always': iconVisibleAlways,
      'clickable-icon': onIconClick
    }),
    onClick: onCardIconClick,
    onKeyDown: function onKeyDown(e) {
      return onEnterKeyPress(e, onCardIconClick);
    },
    tabIndex: onIconClick ? 0 : -1,
    role: onIconClick ? 'button' : 'img',
    "aria-label": iconLabel
  }, icon), onRefresh && /*#__PURE__*/React.createElement(RefreshButton, {
    "aria-label": reloadLabel !== null && reloadLabel !== void 0 ? reloadLabel : intl.formatMessage({
      id: 'embers.card.iconLabel.reload',
      defaultMessage: 'Reload card'
    }),
    onClick: onRefresh
  }));
};
export var EmbersCard = function EmbersCard(_ref2) {
  var _classnames2;
  var headerProps = _ref2.headerProps,
    children = _ref2.children,
    locked = _ref2.locked,
    loading = _ref2.loading,
    title = _ref2.title,
    iconTitle = _ref2.iconTitle,
    titleExtra = _ref2.titleExtra,
    icon = _ref2.icon,
    _ref2$iconVisibleAlwa = _ref2.iconVisibleAlways,
    iconVisibleAlways = _ref2$iconVisibleAlwa === void 0 ? false : _ref2$iconVisibleAlwa,
    withTabs = _ref2.withTabs,
    classes = _ref2.classes,
    onCardClick = _ref2.onCardClick,
    onIconClick = _ref2.onIconClick,
    onRefresh = _ref2.onRefresh,
    reloadLabel = _ref2.reloadLabel,
    iconLabel = _ref2.iconLabel,
    readonly = _ref2.readonly,
    tabIndexProp = _ref2.tabIndex,
    _ref2$contentPadding = _ref2.contentPadding,
    contentPadding = _ref2$contentPadding === void 0 ? [32, 32, 32, 32] : _ref2$contentPadding,
    _ref2$headerPadding = _ref2.headerPadding,
    headerPadding = _ref2$headerPadding === void 0 ? [12, 12, 11, 32] : _ref2$headerPadding,
    role = _ref2.role,
    componentType = _ref2.componentType,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var intl = useIntl();
  // if onCardClick prop is present, the whole card is clickable and
  //  none of the internal members will be interactive
  var wholeCardGetsClicks = !!onCardClick;
  var isReadOnly = readonly || wholeCardGetsClicks;
  var cardIsClickable = wholeCardGetsClicks && !loading;
  var isEditable = !isReadOnly && !loading && !locked;
  var handleCardClicked = function handleCardClicked(e) {
    if (cardIsClickable) {
      e === null || e === void 0 || e.stopPropagation();
      onCardClick();
    }
  };
  var handleCardKeydown = function handleCardKeydown(e) {
    if (cardIsClickable) {
      switch (e.key) {
        case 'Enter':
        case ' ':
          handleCardClicked();
          break;
        default:
          break;
      }
    }
  };
  var cardIcon = icon ? icon : locked ? /*#__PURE__*/React.createElement("i", {
    role: "none",
    className: "icon-lock-fill"
  }) : isEditable ? /*#__PURE__*/React.createElement("i", {
    role: "none",
    className: "icon-edit-fill"
  }) : null;
  var iconIsClickable = !!onIconClick && !(locked || loading);
  var cardClasses = classnames((_classnames2 = {}, _defineProperty(_classnames2, "".concat(EMBERS_CARD_ROOT_PREFIX, "-root"), true), _defineProperty(_classnames2, "".concat(EMBERS_CARD_ROOT_PREFIX, "-readonly"), isReadOnly || loading), _defineProperty(_classnames2, "".concat(EMBERS_CARD_ROOT_PREFIX, "-locked"), locked), _defineProperty(_classnames2, "".concat(EMBERS_CARD_ROOT_PREFIX, "-editable"), isEditable), _defineProperty(_classnames2, "".concat(EMBERS_CARD_ROOT_PREFIX, "-clickable"), cardIsClickable), _defineProperty(_classnames2, "".concat(EMBERS_CARD_ROOT_PREFIX, "-tabbed-card"), withTabs), _classnames2));
  var withEmbersRootClass = classnames(classes === null || classes === void 0 ? void 0 : classes.root, cardClasses);
  var cardTabIndex = -1;
  if (wholeCardGetsClicks) {
    cardTabIndex = tabIndexProp !== undefined ? tabIndexProp : 0;
  }
  var hasHeader = !!headerProps;
  var cardTheme = createEmbersCardTheme(contentPadding, headerPadding, hasHeader);
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: cardTheme
  }, /*#__PURE__*/React.createElement(Card, _extends({
    role: cardIsClickable ? role || 'button' : role,
    onClick: handleCardClicked,
    onKeyDown: handleCardKeydown,
    classes: _objectSpread(_objectSpread({}, classes), {}, {
      root: withEmbersRootClass
    }),
    tabIndex: cardTabIndex
  }, otherProps), hasHeader && /*#__PURE__*/React.createElement(CardHeader, headerProps), /*#__PURE__*/React.createElement(CardContent, null, (title || iconTitle) && /*#__PURE__*/React.createElement(CardContentHeader, {
    title: title,
    iconTitle: iconTitle,
    titleExtra: titleExtra,
    icon: cardIcon,
    iconLabel: iconLabel || (isEditable ? intl.formatMessage({
      id: 'embers.card.iconLabel.edit',
      defaultMessage: 'Edit card'
    }) : void 0) || (locked ? intl.formatMessage({
      id: 'embers.card.iconLabel.lock',
      defaultMessage: 'Locked card'
    }) : void 0),
    onIconClick: iconIsClickable ? onIconClick : void 0,
    iconVisibleAlways: isTouchDevices() ? true : iconVisibleAlways,
    onRefresh: onRefresh,
    reloadLabel: reloadLabel,
    componentType: componentType
  }), loading && /*#__PURE__*/React.createElement("div", {
    className: "".concat(EMBERS_CARD_ROOT_PREFIX, "-content-loading")
  }, /*#__PURE__*/React.createElement(EmbersLoading, {
    size: 72
  })), !loading && /*#__PURE__*/React.createElement("div", {
    className: "".concat(EMBERS_CARD_ROOT_PREFIX, "-content-body")
  }, children))));
};